import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Divider, Stack, Typography } from "@mui/material";
import useGetMemberships from "../../../Common/hooks/useGetMemberships";
import {
  findMembershipByMemberIdentifier,
  getMembershipCallCentreServicePhoneChannel,
  getMembershipCardConfigFromMembership,
  Membership,
  MembershipChangeRequest,
  MembershipChangeRequestFormType,
  MembershipType,
} from "../../../../services/core-api-adapter";
import LoadingSpinner from "../../../Common/components/LoadingSpinner";
import MembershipCard from "../../../Common/components/MembershipCard";
import Panel from "../../../Common/components/Panel";
import MembershipOptionsModal from "../MembershipOptionsModal";
import MembershipDetailDependentsList from "../MembershipDetailDependentsList";
import SlideUpDialog from "../../../Common/components/SlideUpDialog";
import MembershipManagementChangeRequestForm, {
  MembershipChangeRequestActionType,
} from "../MembershipManagementChangeRequestForm";
import IconLoader from "../../../Common/components/IconLoader";
import theme from "../../../../theme";
import {
  FeatureToggleIdentifier,
  isFeatureEnabled,
} from "@/services/feature-toggle-adapter";
import MembershipDetailDependentsListV2 from "../MembershipDetailDependentsListV2";

interface MembershipDetailPanelProps {
  membershipIdentifier?: string;
  membership?: Membership;
  membershipsFetchError?: boolean;
  isMembershipsLoading?: boolean;
}

export default function MembershipDetailPanelV2({
  membershipIdentifier = "",
  membership,
  membershipsFetchError,
  isMembershipsLoading,
}: MembershipDetailPanelProps) {
  const { t } = useTranslation();

  const [mainMemberMembership, setMainMemberMembership] =
    useState<Membership | null>(null);
  const [callCenterNumber, setCallCenterNumber] = useState<string | null>("");
  const [membershipChangeRequest, setMembershipChangeRequest] =
    useState<MembershipChangeRequest | null>(null);
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false);

  useEffect(() => {
    if (membership) {
      setMainMemberMembership(membership);
      setCallCenterNumber(
        getMembershipCallCentreServicePhoneChannel(membership)?.value || null
      );
    }
  }, [membership]);

  function onOptionsButtonClick() {
    setIsOptionsModalOpen(true);
  }

  function onAddDependentButtonClick() {
    setMembershipChangeRequest({
      type: MembershipChangeRequestFormType.ADD_DEPENDENT_FORM,
      formData: {
        membershipIdentifier: membershipIdentifier,
      },
    });
  }

  function shouldShowAddPeopleButton() {
    return mainMemberMembership?.productDetails?.allowDependents;
  }

  const sortedDependents = mainMemberMembership?.members.toSorted((a) =>
    a.membershipDetails.type === MembershipType.MAIN_MEMBER ? -1 : 0
  );

  const isDependentsListShown =
    sortedDependents &&
    (mainMemberMembership?.productDetails?.allowDependents ||
      sortedDependents.length > 0);

  return (
    <>
      <Panel>
        {isMembershipsLoading ? (
          <Stack alignItems="center">
            <LoadingSpinner />
          </Stack>
        ) : (
          <Stack spacing={2}>
            {!mainMemberMembership || membershipsFetchError ? (
              <Typography variant="h4" align="center">
                {t("common.somethingWentWrong")}
              </Typography>
            ) : (
              <>
                <Stack>
                  <MembershipCard
                    config={getMembershipCardConfigFromMembership(
                      mainMemberMembership
                    )}
                  />
                </Stack>

                {callCenterNumber && (
                  <Stack
                    flexDirection="row"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Typography
                      variant="body2"
                      color={theme.palette.neutral[500]}
                    >
                      {t("common.needHelp")}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {callCenterNumber}
                    </Typography>
                  </Stack>
                )}

                {isDependentsListShown && (
                  <>
                    <Button
                      size="medium"
                      variant="outlined"
                      onClick={onOptionsButtonClick}
                    >
                      {t("MembershipDetailPanel.optionsButton")}
                    </Button>

                    <Divider sx={{ mx: 2 }} />
                    {isFeatureEnabled(
                      FeatureToggleIdentifier.ENABLE_UNIFIED_CARE
                    ) ? (
                      <MembershipDetailDependentsListV2
                        dependents={sortedDependents}
                      />
                    ) : (
                      <MembershipDetailDependentsList
                        dependents={sortedDependents}
                      />
                    )}
                  </>
                )}

                {shouldShowAddPeopleButton() === true && (
                  <Button
                    size="small"
                    fullWidth
                    startIcon={<IconLoader icon="PlusIcon" />}
                    onClick={onAddDependentButtonClick}
                  >
                    {t("MembershipDetailPanel.addDependentsButton")}
                  </Button>
                )}
              </>
            )}
          </Stack>
        )}
      </Panel>

      <MembershipOptionsModal
        isOpen={isOptionsModalOpen}
        onClose={() => setIsOptionsModalOpen(false)}
        membership={mainMemberMembership}
      />

      <SlideUpDialog
        isOpen={
          membershipChangeRequest?.type ===
          MembershipChangeRequestFormType.ADD_DEPENDENT_FORM
        }
        onClose={() => setMembershipChangeRequest(null)}
        label={t("MembershipManagementAddDependentForm.title")}
      >
        <MembershipManagementChangeRequestForm
          membershipIdentifier={membershipIdentifier}
          onCancel={() => setMembershipChangeRequest(null)}
          onClose={() => setMembershipChangeRequest(null)}
          membershipActionType={MembershipChangeRequestActionType.ADD_DEPENDENT}
          Icon={
            <IconLoader
              icon="GroupOfFacesIcon"
              sx={{ fontSize: (theme) => theme.spacing(10) }}
            />
          }
        />
      </SlideUpDialog>
    </>
  );
}
